import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const SellYourHomeWestminsterPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Westminster&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching Westminster properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Sell Your Home Westminster CA | Top Listing Agent | Truc Doan</title>
        <meta name="description" content="Sell your home in Westminster CA with top listing agent Truc Doan. Get a free CMA, professional staging, photography, and access to a vast Vietnamese buyer network. Average 22 days to sell at 98-101% list-to-sale ratio." />
        <meta name="keywords" content="sell home Westminster CA, Westminster listing agent, sell house Westminster, CMA Westminster, home staging Westminster, Truc Doan realtor, Vietnamese buyer network, Little Saigon real estate, Westminster home value, sell fast Westminster CA" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/sell-your-home-westminster`} />
        <meta property="og:title" content="Sell Your Home Westminster CA | Top Listing Agent | Truc Doan" />
        <meta property="og:description" content="Sell your Westminster home fast with Truc Doan. Free CMA, professional photography, staging, and access to Vietnamese buyer network. Avg 22 days to sell." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/sell-your-home-westminster`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Westminster, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": "+1-818-818-0989",
            "email": "doanestate@gmail.com",
            "url": `${SEO_CONFIG.DOMAIN}/sell-your-home-westminster`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Top listing agent in Westminster CA specializing in selling homes fast with professional marketing, staging, and Vietnamese buyer network access.",
            "areaServed": {
              "@type": "City",
              "name": "Westminster, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Sell Your Home in Westminster, CA – Top Listing Agent Truc Doan</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Get top dollar for your Westminster home with expert marketing, professional staging, and access to a vast Vietnamese buyer network.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>Why Sell With Truc Doan?</h2>
          <p>Selling your home in Westminster requires an agent who understands the local market intimately. Truc Doan has deep roots in the Westminster and Little Saigon community, with an extensive network of qualified buyers actively searching for homes in this area. Her bilingual English-Vietnamese expertise means your listing reaches the widest possible audience.</p>
          <p>Westminster homes are in high demand, with average sale prices around $750,000 and properties selling in just 22 days on average. Sellers consistently achieve 98-101% of their asking price when working with Truc's proven marketing strategy.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Our Selling Services</h2>
          <p>Every listing receives Truc Doan's full-service marketing package designed to maximize your home's value and minimize time on market.</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>Free Comparative Market Analysis (CMA)</h3>
              <p>Get an accurate home valuation based on recent Westminster sales, current competition, and market trends to price your home strategically for maximum return.</p>
            </div>
            <div className="amenity">
              <h3>Professional Staging & Photography</h3>
              <p>Transform your home with professional staging and high-quality photography including drone shots, virtual tours, and twilight photography that makes buyers fall in love online.</p>
            </div>
            <div className="amenity">
              <h3>Strategic Marketing Plan</h3>
              <p>Multi-channel marketing across MLS, social media, Vietnamese community networks, print media, and targeted digital advertising to reach qualified buyers fast.</p>
            </div>
            <div className="amenity">
              <h3>Vietnamese Buyer Network</h3>
              <p>Tap into Westminster's thriving Vietnamese community with bilingual marketing materials, community outreach, and direct connections to pre-qualified Vietnamese-American buyers.</p>
            </div>
            <div className="amenity">
              <h3>Expert Negotiation</h3>
              <p>Skilled negotiation to ensure you receive the highest possible price with the best terms. Average list-to-sale ratio of 98-101% proves results.</p>
            </div>
            <div className="amenity">
              <h3>Fast Sales Timeline</h3>
              <p>With an average of just 22 days to sell, Truc's marketing strategy gets your home sold quickly without sacrificing price.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Westminster Seller Market Snapshot</h2>
          <p>Westminster is a strong seller's market with high demand driven by proximity to Little Saigon, excellent schools, and a vibrant community.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Average Sale Price</td><td>~$750,000</td></tr>
              <tr><td>Average Days to Sell</td><td>22 Days</td></tr>
              <tr><td>List-to-Sale Price Ratio</td><td>98–101%</td></tr>
              <tr><td>Market Condition</td><td>Seller's Market</td></tr>
              <tr><td>Most Active Price Range</td><td>$600K – $900K</td></tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Recently Listed in Westminster</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">{formatPropertyAddress(property)}, CA {property.postal_code}</div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>Browse all available Westminster listings by clicking "Search Listings" below.</p>
          )}
        </section>

        <section className="cta-section">
          <h2>Ready to Sell Your Westminster Home?</h2>
          <p>Contact Truc Doan today for a free, no-obligation home valuation and learn how much your Westminster property is worth in today's market.</p>
          <div className="cta-buttons">
            <a href="mailto:doanestate@gmail.com?subject=Sell My Westminster Home" className="cta-button primary">Get Free Home Valuation</a>
            <a href="tel:+1-818-818-0989" className="cta-button secondary">Call +1-818-818-0989</a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>Search Listings</button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>
          Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.
        </span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default SellYourHomeWestminsterPage;
